import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { usePresenceStore } from '../../stores/presenceStore';
import api from '../../lib/api';

interface Channel {
  id: string;
  name: string;
  type: string;
}

interface Props {
  user: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    email?: string;
    status?: string;
  };
  position: { x: number; y: number };
  orgId?: string;
  onClose: () => void;
  onDm: () => void;
}

export default function UserPopover({ user, position, orgId, onClose, onDm }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const getStatus = usePresenceStore((s) => s.getStatus);
  const status = getStatus(user.id);
  const [showGroups, setShowGroups] = useState(false);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  const { data: channels = [] } = useQuery<Channel[]>({
    queryKey: ['channels', orgId],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/orgs/${orgId}/channels`);
      return data.data;
    },
    enabled: showGroups && !!orgId,
    staleTime: 30_000,
  });

  const addToGroup = useMutation({
    mutationFn: async (channelId: string) => {
      await api.post(`/api/v1/channels/${channelId}/members`, { userId: user.id });
    },
    onSuccess: (_data, channelId) => {
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
      toast.success(`Added to group`);
    },
    onError: () => toast.error('Could not add to group'),
  });

  const statusText = status === 'ONLINE' ? 'Online' : status === 'AWAY' ? 'Away' : 'Offline';
  const statusColor =
    status === 'ONLINE'
      ? 'bg-accent-green'
      : status === 'AWAY'
        ? 'bg-accent-yellow'
        : 'bg-gray-400';

  const groups = channels.filter((c) => c.type === 'PRIVATE');

  return (
    <div
      ref={ref}
      className="fixed z-50 w-64 rounded-xl border border-white/10 bg-[#111827] shadow-2xl"
      style={{ top: position.y, left: position.x }}
    >
      <div className="p-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-lg font-bold text-white overflow-hidden">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-12 w-12 rounded-xl object-cover" />
            ) : (
              user.displayName[0]?.toUpperCase()
            )}
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-[#111827] ${statusColor}`}
            />
          </div>
          <div>
            <p className="font-semibold text-white">{user.displayName}</p>
            <p className="text-xs text-slate-400">{statusText}</p>
          </div>
        </div>
        {user.email && <p className="mb-3 text-sm text-slate-500">{user.email}</p>}
        <button
          onClick={() => {
            onDm();
            onClose();
          }}
          className="mb-2 w-full rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-hover"
        >
          Send Message
        </button>
        {orgId && (
          <button
            onClick={() => setShowGroups(!showGroups)}
            className="w-full rounded-xl border border-white/10 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-white/5"
          >
            {showGroups ? 'Hide groups' : 'Add to group'}
          </button>
        )}
        {showGroups && groups.length > 0 && (
          <ul className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-white/5 bg-white/[0.03]">
            {groups.map((ch) => (
              <li
                key={ch.id}
                className="flex items-center justify-between border-b border-white/5 px-3 py-2 text-sm last:border-0"
              >
                <span className="truncate text-slate-300">{ch.name.replace(/-/g, ' ')}</span>
                <button
                  onClick={() => addToGroup.mutate(ch.id)}
                  disabled={addToGroup.isPending}
                  className="ml-2 shrink-0 rounded-lg bg-primary px-2 py-0.5 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
                >
                  Add
                </button>
              </li>
            ))}
          </ul>
        )}
        {showGroups && groups.length === 0 && (
          <p className="mt-2 text-center text-xs text-slate-500">No groups available</p>
        )}
      </div>
    </div>
  );
}
